// Shared locale holder for @besiktning/ui.
//
// The web app (or any consumer) calls configureLocale({ locale, currency }) once
// at boot — after it knows the tenant brand — and the shared components +
// formatters below render in that language/currency. Defaults to Swedish/SEK so
// existing behavior is unchanged until configured.

type Lang = "sv" | "en";

let lang: Lang = "sv";
let currency = "SEK";
let timeZone = "Europe/Stockholm";

export function configureLocale(opts: { locale?: string; currency?: string; timeZone?: string }): void {
  if (opts.locale) lang = opts.locale === "en" ? "en" : "sv";
  if (opts.currency) currency = opts.currency;
  if (opts.timeZone) timeZone = opts.timeZone;
}

export function uiLang(): Lang {
  return lang;
}
export function uiCurrency(): string {
  return currency;
}
export function uiLocaleTag(): string {
  return lang === "en" ? "en-GB" : "sv-SE";
}
// Tenant's IANA zone — always pass this to Intl.DateTimeFormat instead of
// relying on the browser/device zone, which may not match the tenant's.
export function uiTimeZone(): string {
  return timeZone;
}

const DICT: Record<Lang, Record<string, string>> = {
  sv: {
    errorTitle: "Något gick fel",
    errorBody: "Ett oväntat fel inträffade.",
    error: "Fel",
    retry: "Försök igen",
    confirm: "Bekräfta",
    cancel: "Avbryt",
    closed: "Stängt",
    justNow: "just nu",
    minutesAgo: "{n} minuter sedan",
    hoursAgo: "{n} timmar sedan",
    daysAgo: "{n} dagar sedan",
    "status.open": "Öppen",
    "status.closed": "Stängd",
    "status.booked": "Bokad",
    "status.pending": "Väntande",
    "status.accepted": "Accepterad",
    "status.rejected": "Avvisad",
    "status.confirmed": "Bekräftad",
    "status.completed": "Genomförd",
    "status.cancelled": "Avbokad",
    "weekday.monday": "Måndag",
    "weekday.tuesday": "Tisdag",
    "weekday.wednesday": "Onsdag",
    "weekday.thursday": "Torsdag",
    "weekday.friday": "Fredag",
    "weekday.saturday": "Lördag",
    "weekday.sunday": "Söndag",
    "serviceCategory.general": "Allmänt underhåll",
    "serviceCategory.oil": "Olja och vätskor",
    "serviceCategory.tires": "Däck och hjul",
    "serviceCategory.brakes": "Bromsar",
    "serviceCategory.engine": "Motor",
    "serviceCategory.electrical": "El-system",
    "serviceCategory.ac": "AC och klimat",
    "serviceCategory.bodywork": "Karosseri",
    "serviceCategory.inspection": "Besiktning",
    "serviceCategory.diagnostics": "Diagnostik",
    "serviceCategory.other": "Övrigt",
    "carCareCategory.exterior_wash": "Extvätt",
    "carCareCategory.interior_cleaning": "Invändig rengöring",
    "carCareCategory.full_detail": "Fullständig tvätt",
    "carCareCategory.wax_polish": "Vax & polering",
    "carCareCategory.tire_rim": "Däck & fälg",
    "carCareCategory.window_cleaning": "Fönsterputs",
    "carCareCategory.engine_cleaning": "Motorrengöring",
    "carCareCategory.other": "Övrigt",
    "cancellationReason.no_show": "Kunden uteblev",
    "cancellationReason.customer_cancelled": "Kunden avbokade",
    "cancellationReason.vehicle_not_delivered": "Fordon ej levererat",
    "cancellationReason.technical_problem": "Tekniskt problem",
    "cancellationReason.staff_illness": "Personal sjukskrivning",
    "cancellationReason.weather": "Väderproblem",
    "cancellationReason.other": "Annat",
    "adTemplate.brand": "Besiktning",
    "adTemplate.navy": "Marin",
    "adTemplate.sunset": "Solnedgång",
    "adTemplate.forest": "Skog",
    "adTemplate.slate": "Kol",
    "adTemplate.minimal": "Minimal",
    noBookingThisDay: "Ingen bokning denna dag",
    until: "till",
  },
  en: {
    errorTitle: "Something went wrong",
    errorBody: "An unexpected error occurred.",
    error: "Error",
    retry: "Try again",
    confirm: "Confirm",
    cancel: "Cancel",
    closed: "Closed",
    justNow: "just now",
    minutesAgo: "{n} minutes ago",
    hoursAgo: "{n} hours ago",
    daysAgo: "{n} days ago",
    "status.open": "Open",
    "status.closed": "Closed",
    "status.booked": "Booked",
    "status.pending": "Pending",
    "status.accepted": "Accepted",
    "status.rejected": "Rejected",
    "status.confirmed": "Confirmed",
    "status.completed": "Completed",
    "status.cancelled": "Cancelled",
    "weekday.monday": "Monday",
    "weekday.tuesday": "Tuesday",
    "weekday.wednesday": "Wednesday",
    "weekday.thursday": "Thursday",
    "weekday.friday": "Friday",
    "weekday.saturday": "Saturday",
    "weekday.sunday": "Sunday",
    "serviceCategory.general": "General maintenance",
    "serviceCategory.oil": "Oil and fluids",
    "serviceCategory.tires": "Tires and wheels",
    "serviceCategory.brakes": "Brakes",
    "serviceCategory.engine": "Engine",
    "serviceCategory.electrical": "Electrical system",
    "serviceCategory.ac": "AC and climate",
    "serviceCategory.bodywork": "Bodywork",
    "serviceCategory.inspection": "Inspection",
    "serviceCategory.diagnostics": "Diagnostics",
    "serviceCategory.other": "Other",
    "carCareCategory.exterior_wash": "Exterior wash",
    "carCareCategory.interior_cleaning": "Interior cleaning",
    "carCareCategory.full_detail": "Full detail",
    "carCareCategory.wax_polish": "Wax & polish",
    "carCareCategory.tire_rim": "Tire & rim",
    "carCareCategory.window_cleaning": "Window cleaning",
    "carCareCategory.engine_cleaning": "Engine cleaning",
    "carCareCategory.other": "Other",
    "cancellationReason.no_show": "Customer no-show",
    "cancellationReason.customer_cancelled": "Customer cancelled",
    "cancellationReason.vehicle_not_delivered": "Vehicle not delivered",
    "cancellationReason.technical_problem": "Technical problem",
    "cancellationReason.staff_illness": "Staff illness",
    "cancellationReason.weather": "Weather problem",
    "cancellationReason.other": "Other",
    "adTemplate.brand": "Brand",
    "adTemplate.navy": "Navy",
    "adTemplate.sunset": "Sunset",
    "adTemplate.forest": "Forest",
    "adTemplate.slate": "Charcoal",
    "adTemplate.minimal": "Minimal",
    noBookingThisDay: "No booking this day",
    until: "until",
  },
};

/** Translate a shared-UI key, with `{n}`-style interpolation. */
export function tUi(key: string, vars?: Record<string, string | number>): string {
  const raw = DICT[lang][key] ?? DICT.sv[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`
  );
}
